import Dialog from "@mui/material/Dialog";
import { useEffect } from "react";
import GuestBadges from "components/GuestBadges";
import { getGuestDetails, resetGuestProfileView } from "store/slice/guestSlice";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";

import { FaCrown, FaTimes } from "react-icons/fa";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

import VuiButton from "components/VuiButton";
import { RiSparkling2Line } from "react-icons/ri";
import useCommon from "hooks/useCommon";
import { generateReservationSummary } from "store/slice/arrivalsSlice";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import usePermissions from "hooks/usePermissions";
import { FLAG_CONFIG } from "variables";

const GuestProfileDialog = ({ open, guest, onClose }) => {
  const { DISPATCH, STORE } = useCommon();
  const { can } = usePermissions();
  const hotelIds = STORE((state) => state.ui.hotelIds);
  const profile = STORE((state) => state.guests.profileViews.inHouse);
  const profileBadges = profile?.details?.data?.guest?.badges ?? profile?.details?.data?.badges;
  const guestId = guest?.id;
  const scope = JSON.stringify(guest?.hotelId ? [guest.hotelId] : hotelIds || []);
  const permitted = can("viewGuests");

  useEffect(() => {
    if (!open || !guestId || !permitted) return;
    const request = DISPATCH(getGuestDetails({ guestId, hotelIds: JSON.parse(scope), profileView: "inHouse" }));
    return () => {
      request.abort();
      DISPATCH(resetGuestProfileView("inHouse"));
    };
  }, [DISPATCH, open, guestId, scope, permitted]);

  const { summaryGenerating, summaryError } = STORE((state) => state.arrivals);
  if (!guest) return null;

  const { black, dark, primary, success, light } = colors;
  const { borderWidth } = borders;

  const initials =
    guest.name
      ?.split(" ")
      .slice(0, 2)
      .map((p) => p[0])
      .join("")
      .toUpperCase() || "G";

  const flagConfig = guest.flag?.type ? FLAG_CONFIG[guest.flag.type] : null;
  const FlagIcon = flagConfig?.icon;

  const handleGenerateSummary = async () => {
    if (!guest?.reservationId) return;

    try {
      await DISPATCH(
        generateReservationSummary({
          reservationId: guest.reservationId,
        })
      ).unwrap();
    } catch (err) {
      console.error("Guest AI summary failed:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* ================= HEADER ================= */}
      <DialogTitle sx={{ pb: 0 }}>
        <VuiBox display="flex" justifyContent="space-between" alignItems="center">
          <VuiTypography variant="lg">Guest Profile</VuiTypography>

          <IconButton size="small" onClick={onClose}>
            <FaTimes color={primary.main} />
          </IconButton>
        </VuiBox>
      </DialogTitle>

      {/* ================= CONTENT ================= */}
      <DialogContent sx={{ pt: 2, mt: 3 }}>
        {/* Identity */}
        <VuiBox display="flex" alignItems="center" gap={2} mb={0}>
          <Avatar
            src={guest.avatarUrl || ""}
            alt={guest.name}
            sx={{
              width: 48,
              height: 48,
              fontSize: 16,
              color: primary.main,
              background: rgba(primary.main, 0.1),
              border: `${borderWidth[1]} solid ${rgba(primary.main, 0.18)}`,
            }}
          >
            {initials}
          </Avatar>

          <VuiBox>
            <VuiTypography variant="h6" sx={{ mb: 0 }}>
              {guest.name}
            </VuiTypography>
            <GuestBadges badges={profileBadges ?? guest.badges} />
            <VuiTypography variant="caption" sx={{ m: 0 }}>
              Room: {guest.room || "—"}
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {/* Stay Info */}
        <VuiBox mb={0}>
          <VuiTypography variant="caption">Arrived</VuiTypography>
          <VuiTypography>
            {guest.arrivedDate
              ? `${guest.arrivedDate}${guest.arrivedTime ? ` · ${guest.arrivedTime}` : ""}`
              : "—"}
          </VuiTypography>
        </VuiBox>

        {/* Loyalty */}
        {guest.loyalty && (
          <VuiBox mb={1} display="flex" flexDirection="column" gap={0.75}>
            <VuiTypography variant="caption">Badge</VuiTypography>

            <VuiBox
              mt={1}
              px={1.5}
              py={0.35}
              borderRadius="md"
              display="inline-flex"
              alignItems="center"
              gap={0.6}
              sx={({ palette: { info, white } }) => ({
                background: info.main,
                color: white.main,
                fontSize: "12px",
                width: "fit-content",
              })}
            >
              <FaCrown size={12} color="#FFD700" />
              {`${guest.loyalty.badgeLabel} Member`}
            </VuiBox>
          </VuiBox>
        )}

        {/* Guest Personality */}

        <VuiBox mb={1}>
          <VuiTypography variant="caption" mb={0.5}>
            Guest Personality
          </VuiTypography>

          <VuiBox
            p={1.75}
            borderRadius="14px"
            sx={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
              border: `1px solid ${rgba(dark.main, 0.25)}`,
            }}
          >
            {/* ERROR */}
            {summaryError && (
              <VuiBox
                mb={1}
                px={1.5}
                py={0.5}
                borderRadius="md"
                sx={{
                  background: "rgba(255, 0, 0, 0.08)",
                  border: "1px solid rgba(255, 0, 0, 0.25)",
                }}
              >
                <VuiTypography variant="caption" color="error">
                  {summaryError?.message || "Failed to generate summary"}
                </VuiTypography>
              </VuiBox>
            )}

            {/* SUMMARY */}
            {guest?.guestSummary ? (
              <VuiTypography variant="body2" lineHeight={1.5} sx={{ fontSize: "14px" }}>
                {guest.guestSummary}
              </VuiTypography>
            ) : can("generateAiSummary") ? (
              <VuiBox display="flex" justifyContent="center" my={1}>
                <VuiButton
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<RiSparkling2Line size={12} />}
                  onClick={handleGenerateSummary}
                  disabled={summaryGenerating || !guest?.reservationId}
                >
                  {summaryGenerating ? "Generating..." : "Generate"}
                </VuiButton>
              </VuiBox>
            ) : (
              <VuiTypography variant="body2">No summary available</VuiTypography>
            )}

            {/* FOOTNOTE */}
            {guest?.guestSummary && (
              <VuiTypography variant="caption" mt={1} sx={{ opacity: 0.75 }}>
                Based on latest stay preferences
              </VuiTypography>
            )}
            {/* REGENERATE */}
            {guest?.guestSummary && can("regenerateAiSummary") && (
              <VuiTypography
                variant="caption"
                mt={1}
                sx={{
                  cursor: summaryGenerating ? "default" : "pointer",
                  opacity: summaryGenerating ? 0.6 : 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
                onClick={!summaryGenerating ? handleGenerateSummary : undefined}
              >
                <RiSparkling2Line size={12} />
                {summaryGenerating ? "Generating..." : "Regenerate"}
              </VuiTypography>
            )}
          </VuiBox>
        </VuiBox>
        {/* <VuiBox mb={1}>
          <VuiTypography variant="caption"  mb={0.5}>
            Guest Personality
          </VuiTypography>

          <VuiBox
            p={1.75}
            borderRadius="14px"
            sx={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <VuiTypography variant="body2"  lineHeight={1.5} sx={{ fontSize: "14px" }}>
              {guest?.guestSummary}
            </VuiTypography>

            {guest?.guestSummary && (
              <VuiTypography variant="caption"  mt={1} sx={{ opacity: 0.75 }}>
                AI-generated summary is based on stay preferences
              </VuiTypography>
            )}
          </VuiBox>
        </VuiBox> */}

        {/* Flag */}
        {flagConfig && (
          <VuiBox mb={2}>
            <VuiTypography variant="caption" mb={0.5}>
              Flag
            </VuiTypography>

            <VuiBox display="flex" alignItems="center" gap={1} mb={0.75}>
              <FlagIcon size={14} color={flagConfig.color} />
              <VuiTypography variant="body2" fontWeight="medium">
                {flagConfig.label}
              </VuiTypography>
            </VuiBox>

            {guest.flag.reason && (
              <VuiTypography variant="body2">{guest.flag.reason}</VuiTypography>
            )}
          </VuiBox>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GuestProfileDialog;
